<template>
  <component :is="embedded ? 'div' : 'v-main'">
    <component :is="embedded ? 'div' : 'v-container'" v-bind="embedded ? {} : { fluid: true }">
      <component :is="embedded ? 'div' : 'v-card'" v-bind="embedded ? {} : { elevation: 6, rounded: 'xxl' }" :class="embedded ? '' : 'app-card'">
        <v-card-title v-if="!embedded">
          <v-btn class="mx-2" fab x-small color="primary" @click="$router.back()">
            <v-icon dark>mdi-arrow-left</v-icon> </v-btn
          >Create Purchase Bill
        </v-card-title>
        <component :is="embedded ? 'div' : 'v-card-text'">
          <v-row>
            <v-col sm="6">
              <v-autocomplete
                v-model="form.purchase_order_id"
                :items="purchaseOrders"
                item-text="label"
                item-value="id"
                outlined
                prepend-inner-icon="mdi-file-document-outline"
                :error-messages="errors.purchase_order_id"
                @change="onPurchaseOrderPick"
              >
                <template v-slot:label>Purchase Order <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-autocomplete>
            </v-col>
            <v-col sm="6">
              <v-text-field
                :value="selectedVendorName"
                outlined
                readonly
                label="Vendor"
                prepend-inner-icon="mdi-domain"
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-menu
                v-model="billDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.bill_date"
                    outlined
                    readonly
                    prepend-inner-icon="mdi-calendar"
                    v-bind="attrs"
                    v-on="on"
                    :error-messages="errors.bill_date"
                  >
                    <template v-slot:label>Bill Date <span style="color:#f44336;font-weight:bold;">*</span></template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="form.bill_date"
                  @input="billDateMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col sm="4">
              <v-menu
                v-model="dueDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.due_date"
                    outlined
                    readonly
                    label="Due Date"
                    prepend-inner-icon="mdi-calendar-clock"
                    v-bind="attrs"
                    v-on="on"
                    :error-messages="errors.due_date"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="form.due_date"
                  @input="dueDateMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col sm="4">
              <v-text-field
                :value="selectedTotalAmount"
                outlined
                readonly
                label="Total Amount"
                prepend-inner-icon="mdi-currency-inr"
                :hint="pendingHint"
                persistent-hint
                class="mono-figure"
              ></v-text-field>
            </v-col>
            <v-col sm="12">
              <v-textarea
                v-model="form.remarks"
                outlined
                label="Remarks"
                prepend-inner-icon="mdi-note-text"
                rows="2"
                auto-grow
                :error-messages="errors.remarks"
              ></v-textarea>
            </v-col>
          </v-row>

          <AppSectionHeader label="Payment Details" />
          <v-row align="center">
            <v-col sm="3">
              <v-text-field
                v-model.number="form.initial_payment_amount"
                type="number"
                min="0.01"
                :max="selectedPendingAmount || null"
                outlined
                dense
                :error-messages="amountError || errors.initial_payment_amount"
                @input="clampAmount"
              >
                <template v-slot:label>Amount <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-text-field>
            </v-col>
            <v-col sm="3">
              <v-menu
                v-model="paymentDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.initial_payment_date"
                    outlined
                    readonly
                    dense
                    v-bind="attrs"
                    v-on="on"
                    :error-messages="errors.initial_payment_date"
                  >
                    <template v-slot:label>Payment Date <span style="color:#f44336;font-weight:bold;">*</span></template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="form.initial_payment_date"
                  @input="paymentDateMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col sm="2">
              <v-select
                v-model="form.initial_payment_mode"
                :items="paymentModeOptions"
                label="Mode"
                outlined
                dense
                :error-messages="errors.initial_payment_mode"
              ></v-select>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model="form.initial_payment_reference"
                label="Reference No."
                outlined
                dense
                :error-messages="errors.initial_payment_reference"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-divider class="mt-5 mb-5"> </v-divider>
          <div class="text-right">
            <v-btn text class="mr-2" @click="embedded ? $emit('close') : $router.back()">Cancel</v-btn>
            <v-btn
              :loading="isLoading"
              :disabled="isLoading || !!amountError"
              rounded
              color="primary"
              dark
              @click="save"
            >
              Create
            </v-btn>
          </div>
        </component>
      </component>
    </component>
  </component>
</template>
<script>
import axios from "axios";
import AppSectionHeader from "@/components/AppSectionHeader.vue";
export default {
  name: "PurchaseBillCreatePage",
  components: { AppSectionHeader },
  props: {
    // When true, this renders inside a dialog (opened from the Purchase
    // Bills list "+" button) instead of its own routed page.
    embedded: { type: Boolean, default: false },
  },
  data() {
    return {
      form: {
        purchase_order_id: null,
        bill_date: new Date().toISOString().substr(0, 10),
        due_date: null,
        remarks: "",
        initial_payment_amount: null,
        initial_payment_date: new Date().toISOString().substr(0, 10),
        initial_payment_mode: 0,
        initial_payment_reference: "",
      },
      billDateMenu: false,
      dueDateMenu: false,
      paymentDateMenu: false,
      vendors: [],
      purchaseOrdersRaw: [],
      paymentModeOptions: [
        { text: "Cash", value: 0 },
        { text: "Bank Transfer", value: 1 },
        { text: "Cheque", value: 2 },
        { text: "UPI", value: 3 },
        { text: "Other", value: 4 },
      ],
      isLoading: false,
    };
  },
  computed: {
    errors() {
      return this.$store.getters["errors/getErrors"] || {};
    },
    purchaseOrders() {
      return this.purchaseOrdersRaw.map((po) => {
        const isPartiallyBilled = Number(po.pending_amount) < Number(po.total_amount);
        return {
          id: po.id,
          vendor_id: po.vendor_id,
          total_amount: po.total_amount,
          pending_amount: po.pending_amount,
          label: isPartiallyBilled
            ? `${po.po_number} - ${this.vendorName(po.vendor_id)} (₹${po.pending_amount} pending of ₹${po.total_amount})`
            : `${po.po_number} - ${this.vendorName(po.vendor_id)} (₹${po.total_amount})`,
        };
      });
    },
    selectedPurchaseOrder() {
      return this.purchaseOrdersRaw.find((p) => p.id === this.form.purchase_order_id);
    },
    selectedVendorName() {
      return this.selectedPurchaseOrder ? this.vendorName(this.selectedPurchaseOrder.vendor_id) : "";
    },
    selectedTotalAmount() {
      return this.selectedPurchaseOrder ? this.selectedPurchaseOrder.total_amount : "";
    },
    selectedPendingAmount() {
      return this.selectedPurchaseOrder ? this.selectedPurchaseOrder.pending_amount : "";
    },
    pendingHint() {
      if (!this.selectedPurchaseOrder) {
        return "Always matches the selected purchase order's total";
      }
      if (Number(this.selectedPendingAmount) < Number(this.selectedTotalAmount)) {
        return `₹${this.selectedPendingAmount} still pending on this PO`;
      }
      return "Always matches the selected purchase order's total";
    },
    amountError() {
      if (!this.selectedPendingAmount || !this.form.initial_payment_amount) {
        return "";
      }
      if (Number(this.form.initial_payment_amount) > Number(this.selectedPendingAmount)) {
        return `Amount cannot exceed the pending balance of ₹${this.selectedPendingAmount}`;
      }
      return "";
    },
  },
  mounted() {
    this.$store.dispatch("errors/setErrors", {});
    this.getMasters();
  },
  methods: {
    async getMasters() {
      await axios
        .get(`masters?context=purchase_bill`)
        .then((response) => {
          this.vendors = response.data.vendors || [];
          this.purchaseOrdersRaw = response.data.purchase_orders || [];
        })
        .catch(function (error) {
          console.log(error);
        });
    },
    vendorName(vendorId) {
      const vendor = this.vendors.find((v) => v.id === vendorId);
      return vendor ? vendor.name : "";
    },
    onPurchaseOrderPick() {
      // Total amount now follows the selected PO automatically (readonly display);
      // default the payment amount to whatever's still pending on it (the full
      // total if unbilled) - admin can reduce it, but never raise it past that.
      this.form.initial_payment_amount = this.selectedPendingAmount || null;
    },
    clampAmount() {
      if (this.selectedPendingAmount && this.form.initial_payment_amount > this.selectedPendingAmount) {
        this.form.initial_payment_amount = this.selectedPendingAmount;
      }
    },
    async save() {
      try {
        this.isLoading = true;
        this.$store.dispatch("errors/setErrors", {});
        await axios.post("/purchase_bills", this.form);
        this.isLoading = false;
        if (this.embedded) {
          this.$emit("saved");
          this.$emit("close");
        } else {
          this.$router.push("/purchase-bills");
        }
      } catch (e) {
        console.log(e);
        this.isLoading = false;
        if (e.response && e.response.data && e.response.data.errors) {
          this.$store.dispatch("errors/setErrors", e.response.data.errors);
        }
        if (e.response && e.response.data && e.response.data.message) {
          this.$store.dispatch("errors/setErrors", {
            initial_payment_amount: [e.response.data.message],
          });
        }
      }
    },
  },
};
</script>
